import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import {
  RiArrowLeftLine,
  RiCheckboxCircleLine,
  RiPlaneLine,
  RiUserLine,
  RiShieldLine,
  RiAlertLine,
  RiLockUnlockLine,
  RiCalendarLine,
  RiBuildingLine,
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiReceiptLine,
  RiArrowRightLine,
  RiCloseCircleLine,
  RiTimeLine,
  RiLoader4Line,
  RiRefreshLine,
  RiCheckDoubleLine,
  RiEyeLine,
  RiInformationLine,
  RiTimerLine
} from "react-icons/ri";

const SeriesFarePayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { fare, searchQuery, bookingPax } = location.state || {};

  const [isProcessing, setIsProcessing] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingFailed, setBookingFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [statusPolling, setStatusPolling] = useState(null);

  const statusPollInterval = useRef(null);

  useEffect(() => {
    if (!fare || !bookingPax) {
      navigate("/seriesfare");
    }

    // Cleanup on unmount
    return () => {
      if (statusPollInterval.current) {
        clearInterval(statusPollInterval.current);
      }
    };
  }, [fare, bookingPax, navigate]);

  useEffect(() => {
    if (bookingId && bookingDetails?.bookingStatus === "BLOCK REQUEST") {
      // Start polling for status updates
      startStatusPolling(bookingId);
    } else {
      // Stop polling if booking is confirmed or cancelled
      if (statusPollInterval.current) {
        clearInterval(statusPollInterval.current);
        setStatusPolling(false);
      }
    }

    return () => {
      if (statusPollInterval.current) {
        clearInterval(statusPollInterval.current);
      }
    };
  }, [bookingId, bookingDetails]);

  if (!fare || !bookingPax) return null;

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `INR ${Math.round(num).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const calculateTotal = () => {
    if (!fare || !bookingPax) return 0;
    const pricePerPerson = parseFloat(fare.pricePerPerson) || 0;
    return pricePerPerson * bookingPax;
  };

  const startStatusPolling = (bookingId) => {
    if (statusPollInterval.current) {
      clearInterval(statusPollInterval.current);
    }

    statusPollInterval.current = setInterval(async () => {
      try {
        const details = await getBookingDetails(bookingId);
        if (details) {
          setBookingDetails(details);
          
          // Stop polling if status changed from BLOCK REQUEST
          if (details.bookingStatus !== "BLOCK REQUEST") {
            clearInterval(statusPollInterval.current);
            setStatusPolling(false);
            
            // If rejected, show error
            if (details.bookingStatus === "REJECTED") {
              setErrorMessage("Your block request was rejected by DMC. Please try another fare.");
            }
          }
        }
      } catch (error) {
        console.error("Status polling error:", error);
      }
    }, 10000); // Poll every 10 seconds

    setStatusPolling(true);
  };

  // Step 1: Create booking with BLOCK REQUEST status
  const createBlockBooking = async () => {
    try {
      const createBookingDto = {
        seriesFareId: fare.id,
        pax: bookingPax,
        agentId: user?.cid
      };

      console.log("Creating block booking with DTO:", createBookingDto);

      const response = await apiService.post(
        config.SERIESBOOKINGCREATE,
        createBookingDto
      );

      console.log("Block booking response:", response);

      if (response && response.success) {
        const bookingId = response.bookingId;
        
        if (bookingId) {
          setBookingId(bookingId);
          
          // Fetch booking details to show current status
          const bookingDetails = await getBookingDetails(bookingId);
          setBookingDetails(bookingDetails);
          
          return bookingId;
        } else {
          throw new Error("Booking created but no booking ID returned");
        }
      } else {
        throw new Error(response?.message || "Failed to create booking");
      }
    } catch (error) {
      console.error("Error creating block booking:", error);
      throw error;
    }
  };

  // Get booking details
  const getBookingDetails = async (bookingId) => {
    try {
      const response = await apiService.get(`${config.SERIESBOOKING}/${bookingId}`);
      if (response && response.success) {
        return response.booking;
      }
      return null;
    } catch (error) {
      console.error("Error fetching booking details:", error);
      return null;
    }
  };

  // Check booking status
  const checkBookingStatus = async (bookingId) => {
    try {
      const booking = await getBookingDetails(bookingId);
      return booking ? booking.bookingStatus : null;
    } catch (error) {
      console.error("Error checking booking status:", error);
      return null;
    }
  };

  // Cancel block request
  const cancelBlockRequest = async () => {
    if (!bookingId) return;

    try {
      const response = await apiService.post(
        config.SERIESBOOKINGCANCEL,
        { bookingId }
      );

      if (response && response.success) {
        setBookingId(null);
        setBookingDetails(null);
        setErrorMessage("Block request cancelled successfully.");
      } else {
        throw new Error(response?.message || "Failed to cancel block request");
      }
    } catch (error) {
      console.error("Error cancelling block request:", error);
      setErrorMessage(error.message || "Failed to cancel block request");
    }
  };

  // Handle payment success
  const handlePaymentSuccess = async (paymentResponse) => {
    try {
      // Update payment status to SUCCESS
      const updateResponse = await apiService.put(
        config.SERIESBOOKINGPAYMENT,
        {
          bookingId: bookingId,
          paymentStatus: "SUCCESS"
        }
      );

      if (updateResponse && updateResponse.success) {
        setBookingSuccess(true);
        
        // Get updated booking details
        const updatedBooking = await getBookingDetails(bookingId);
        
        const confirmationData = {
          bookingId: bookingId,
          fare: fare,
          searchQuery: searchQuery,
          bookingPax: bookingPax,
          totalAmount: calculateTotal(),
          pricePerPerson: fare.pricePerPerson,
          bookingStatus: updatedBooking?.bookingStatus || "CONFIRMED",
          paymentStatus: "SUCCESS",
          paymentDetails: {
            paymentId: paymentResponse.razorpay_payment_id,
            orderId: paymentResponse.razorpay_order_id,
            signature: paymentResponse.razorpay_signature,
            amount: calculateTotal(),
            timestamp: new Date().toISOString()
          }
        };
        
        setTimeout(() => {
          navigate("/seriesfare-confirmation", { state: confirmationData });
        }, 2000);
      } else {
        throw new Error("Failed to update payment status");
      }
    } catch (error) {
      console.error("Error:", error);
      setErrorMessage("Payment successful but confirmation failed.");
      setBookingSuccess(false);
      setBookingFailed(true);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle payment failure
  const handlePaymentFailure = async (error) => {
    setBookingFailed(true);
    setErrorMessage(error?.description || "Payment failed. Please try again.");
    
    // Update payment status to FAILED
    try {
      await apiService.put(
        config.SERIESBOOKINGPAYMENT,
        {
          bookingId: bookingId,
          paymentStatus: "FAILED"
        }
      );
    } catch (updateError) {
      console.error("Failed to update payment status:", updateError);
    } finally {
      setIsProcessing(false);
    }
  };

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Main booking and payment flow
  const initiateBookingFlow = async () => {
    setIsProcessing(true);
    setErrorMessage(null);
    setBookingFailed(false);

    try {
      // If no booking ID exists, create a new block request
      if (!bookingId) {
        const newBookingId = await createBlockBooking();
        if (!newBookingId) {
          throw new Error("Failed to create booking");
        }
        
        // Booking created successfully, wait for DMC confirmation
        setIsProcessing(false);
        return;
      }

      // If booking exists, check its status
      const status = await checkBookingStatus(bookingId);
      
      if (!status) {
        throw new Error("Unable to check booking status");
      }

      switch (status) {
        case "BLOCK REQUEST":
          setErrorMessage("Booking is awaiting DMC confirmation. Please wait...");
          setIsProcessing(false);
          break;
          
        case "BLOCKED":
          // Proceed to payment
          await initiatePayment();
          break;
          
        case "REJECTED":
          setErrorMessage("Your block request was rejected by DMC. Please try another fare.");
          setIsProcessing(false);
          break;
          
        case "CONFIRMED":
          setErrorMessage("This booking is already confirmed.");
          setIsProcessing(false);
          break;
          
        case "CANCELLED":
          setErrorMessage("This booking was cancelled.");
          setIsProcessing(false);
          break;
          
        default:
          setErrorMessage(`Unknown booking status: ${status}`);
          setIsProcessing(false);
          break;
      }
    } catch (error) {
      console.error("Booking error:", error);
      setErrorMessage(error.message || "Booking process failed.");
      setIsProcessing(false);
    }
  };

  // Initiate payment
  const initiatePayment = async () => {
    const res = await loadRazorpayScript();
    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      setIsProcessing(false);
      return;
    }

    const totalAmount = calculateTotal();
    const finalAmount = totalAmount * 100;

    try {
      // Create payment order
      const paymentResult = await apiService.post(
        `${config.MAKEPAYMENT}?Amount=${totalAmount}&PayRequest=RAZORPAY&CompanyId=${user?.cid}`
      );

      if (!paymentResult.succeeded) {
        setErrorMessage("Payment initiation failed.");
        setIsProcessing(false);
        return;
      }

      const { id: order_id, currency } = paymentResult.data;

      const options = {
        key: paymentResult.data.razorpayKey || 'rzp_test_your_key',
        amount: finalAmount,
        currency: currency,
        name: "Smart Travel Series Fares",
        description: `Series Fare - ${fare.airlineCode} | ${fare.from} to ${fare.to} | ${bookingPax} Pax`,
        order_id: order_id,
        handler: async function (paymentResponse) {
          try {
            await handlePaymentSuccess(paymentResponse);
          } catch (error) {
            await handlePaymentFailure(error);
          }
        },
        prefill: {
          email: user?.email,
          contact: user?.phoneNumber || user?.phone,
          name: user?.companyName || user?.firstName + " " + user?.lastName
        },
        notes: {
          bookingId: bookingId,
          bookingType: "SERIES_FARE",
          fareId: fare.seriesFareId || fare.id,
          pax: bookingPax,
          airline: fare.airlineCode,
          agentId: user?.cid
        },
        theme: { color: "#3399cc" }
      };

      const paymentObject = new window.Razorpay(options);

      paymentObject.on("payment.failed", async function (response) {
        await handlePaymentFailure(response.error);
      });

      paymentObject.on("payment.cancel", function () {
        setErrorMessage("Payment was cancelled.");
        setIsProcessing(false);
      });

      paymentObject.open();
    } catch (error) {
      console.error("Payment error:", error);
      setErrorMessage(error.message || "Payment initiation failed.");
      setIsProcessing(false);
    }
  };

  const handleBack = () => navigate(-1);
  const handleRetry = () => {
    setBookingFailed(false);
    setErrorMessage(null);
    setBookingId(null);
    setBookingDetails(null);
  };

  const handleViewBookingStatus = async () => {
    if (!bookingId) return;
    
    setCheckingStatus(true);
    try {
      const details = await getBookingDetails(bookingId);
      if (details) {
        setBookingDetails(details);
      }
    } catch (error) {
      console.error("Error checking status:", error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const totalAmount = calculateTotal();

  const getStatusBadge = (status) => {
    switch (status) {
      case "BLOCK REQUEST":
        return { class: "bg-warning", text: "BLOCK REQUEST" };
      case "BLOCKED":
        return { class: "bg-success", text: "BLOCKED" };
      case "CONFIRMED":
        return { class: "bg-primary", text: "CONFIRMED" };
      case "REJECTED":
        return { class: "bg-danger", text: "REJECTED" };
      case "CANCELLED":
        return { class: "bg-secondary", text: "CANCELLED" };
      default:
        return { class: "bg-light text-dark", text: status };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "BLOCK REQUEST":
        return <RiTimeLine className="me-2" />;
      case "BLOCKED":
        return <RiCheckboxCircleLine className="me-2" />;
      case "CONFIRMED":
        return <RiCheckDoubleLine className="me-2" />;
      case "REJECTED":
        return <RiCloseCircleLine className="me-2" />;
      default:
        return <RiInformationLine className="me-2" />;
    }
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Page Header */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-1">Series Fare Booking</h3>
                    <p className="text-muted mb-0">Block seats and complete payment</p>
                  </div>
                  <button className="btn btn-outline-secondary" onClick={handleBack} disabled={isProcessing}>
                    <RiArrowLeftLine className="me-1" /> Back to Results
                  </button>
                </div>
              </div>
            </div>

            {/* Booking Status Card */}
            {bookingDetails && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className={`card ${
                    bookingDetails.bookingStatus === "BLOCK REQUEST" ? "border-warning" :
                    bookingDetails.bookingStatus === "BLOCKED" ? "border-success" :
                    bookingDetails.bookingStatus === "CONFIRMED" ? "border-primary" :
                    bookingDetails.bookingStatus === "REJECTED" ? "border-danger" :
                    "border-secondary"
                  }`}>
                    <div className="card-body">
                      <div className="row align-items-center">
                        <div className="col-md-4">
                          <h6 className="mb-2">Booking Status</h6>
                          <div className={`badge ${getStatusBadge(bookingDetails.bookingStatus).class} fs-6 p-2`}>
                            {getStatusIcon(bookingDetails.bookingStatus)}
                            {getStatusBadge(bookingDetails.bookingStatus).text}
                          </div>
                          {statusPolling && (
                            <div className="mt-2">
                              <small className="text-warning">
                                <RiLoader4Line className="me-1 spinning" />
                                Checking DMC confirmation...
                              </small>
                            </div>
                          )}
                        </div>
                        <div className="col-md-4">
                          <h6 className="mb-2">Payment Status</h6>
                          <div className={`badge ${
                            bookingDetails.paymentStatus === "PENDING" ? "bg-warning" :
                            bookingDetails.paymentStatus === "SUCCESS" ? "bg-success" :
                            bookingDetails.paymentStatus === "FAILED" ? "bg-danger" : "bg-light text-dark"
                          } fs-6 p-2`}>
                            {bookingDetails.paymentStatus}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <h6 className="mb-2">Booking Reference</h6>
                          <code className="fs-5 d-block mb-2">{bookingId}</code>
                          <small className="text-muted">
                            Created: {new Date(bookingDetails.createdAt).toLocaleString()}
                          </small>
                        </div>
                      </div>
                      
                      {bookingDetails.bookingStatus === "BLOCK REQUEST" && (
                        <div className="alert alert-warning mt-3 mb-0">
                          <RiAlertLine className="me-2" />
                          <strong>Waiting for DMC confirmation:</strong> Your seat block request has been sent to the DMC. 
                          You will be able to proceed with payment once they confirm seat availability.
                          {statusPolling && " (Auto-refreshing...)"}
                        </div>
                      )}
                      
                      {bookingDetails.bookingStatus === "BLOCKED" && (
                        <div className="alert alert-success mt-3 mb-0">
                          <RiCheckboxCircleLine className="me-2" />
                          <strong>Seats Blocked!</strong> DMC has confirmed seat availability. 
                          You can now proceed with payment to confirm your booking.
                        </div>
                      )}
                      
                      {bookingDetails.bookingStatus === "REJECTED" && (
                        <div className="alert alert-danger mt-3 mb-0">
                          <RiCloseCircleLine className="me-2" />
                          <strong>Block Request Rejected:</strong> DMC was unable to confirm the requested seats. 
                          Please try another fare or contact support.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Form */}
            {!bookingSuccess && !bookingFailed && (
              <div className="row">
                <div className="col-lg-8">
                  {/* Flight Details */}
                  <div className="card mb-4">
                    <div className="card-header bg-light">
                      <h5 className="mb-0"><RiPlaneLine className="me-2" /> Flight Details</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <small className="text-muted">Route</small>
                            <h5>
                              <RiFlightTakeoffLine className="me-2 text-primary" />
                              {fare.from} <RiArrowRightLine className="mx-2" />
                              <RiFlightLandLine className="me-2" /> {fare.to}
                            </h5>
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">Airline</small>
                            <p className="mb-0"><strong>{fare.airlineCode}</strong></p>
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">Departure Date</small>
                            <p className="mb-0"><RiCalendarLine className="me-2" />{formatDate(fare.departureDate)}</p>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <small className="text-muted">Cabin Class</small>
                            <p className="mb-0"><span className="badge bg-info">{fare.cabinClass || "ECONOMY"}</span></p>
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">Validity</small>
                            <p className="mb-0">{formatDate(fare.validFrom)} to {formatDate(fare.validTo)}</p>
                          </div>
                          <div className="mb-3">
                            <small className="text-muted">DMC Provider</small>
                            <p className="mb-0"><RiBuildingLine className="me-2" />{fare.companyName || "Direct Supplier"}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Booking Summary */}
                  <div className="card">
                    <div className="card-header bg-light">
                      <h5 className="mb-0"><RiReceiptLine className="me-2" /> Booking Summary</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <small className="text-muted">Number of Passengers</small>
                            <h5><RiUserLine className="me-2" />{bookingPax}</h5>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <small className="text-muted">Price per Person</small>
                            <h5 className="text-success">{formatCurrency(fare.pricePerPerson)}</h5>
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-12">
                          <div className="bg-light p-3 rounded">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>Total Amount</strong>
                                <div className="small text-muted">
                                  {bookingPax} passenger{bookingPax > 1 ? 's' : ''}
                                </div>
                              </div>
                              <div>
                                <h3 className="text-primary mb-0">{formatCurrency(totalAmount)}</h3>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div className="sticky-top" style={{ top: "20px" }}>
                    <div className="card border-primary mb-4">
                      <div className="card-header bg-primary text-white">
                        <h5 className="mb-0">Booking & Payment</h5>
                      </div>
                      <div className="card-body">
                        {/* Status Steps */}
                        <div className="mb-4">
                          <div className="d-flex align-items-center mb-3">
                            <div className={`rounded-circle p-2 ${
                              bookingDetails?.bookingStatus === "BLOCK REQUEST" ? "bg-warning" :
                              bookingDetails?.bookingStatus === "BLOCKED" || 
                              bookingDetails?.bookingStatus === "CONFIRMED" ? "bg-success" : 
                              bookingDetails?.bookingStatus === "REJECTED" ? "bg-danger" : "bg-secondary"
                            } text-white me-3`}>
                              <RiCheckboxCircleLine size={20} />
                            </div>
                            <div>
                              <h6 className="mb-0">Step 1: Block Seats</h6>
                              <small className="text-muted">
                                {bookingDetails?.bookingStatus === "BLOCK REQUEST" 
                                  ? "Awaiting DMC confirmation" 
                                  : bookingDetails?.bookingStatus === "BLOCKED"
                                  ? "Seats blocked successfully"
                                  : bookingDetails?.bookingStatus === "REJECTED"
                                  ? "Request rejected"
                                  : "Initiate booking"}
                              </small>
                            </div>
                          </div>
                          
                          <div className="d-flex align-items-center">
                            <div className={`rounded-circle p-2 ${
                              bookingDetails?.paymentStatus === "SUCCESS" ? "bg-success" : "bg-secondary"
                            } text-white me-3`}>
                              <RiLockUnlockLine size={20} />
                            </div>
                            <div>
                              <h6 className="mb-0">Step 2: Make Payment</h6>
                              <small className="text-muted">
                                {bookingDetails?.paymentStatus === "SUCCESS" 
                                  ? "Payment completed" 
                                  : bookingDetails?.bookingStatus === "BLOCKED"
                                  ? "Ready for payment"
                                  : "Complete payment to confirm booking"}
                              </small>
                            </div>
                          </div>
                        </div>

                        {/* Price Summary */}
                        <div className="mb-4">
                          <div className="d-flex justify-content-between mb-2">
                            <div>
                              <span>Price per Person</span>
                              <div className="small text-muted">{formatCurrency(fare.pricePerPerson)} × {bookingPax}</div>
                            </div>
                            <div className="fw-bold">{formatCurrency(totalAmount)}</div>
                          </div>
                          <hr />
                          <div className="d-flex justify-content-between">
                            <div>
                              <strong>Total Amount</strong>
                              <div className="small text-muted">{bookingPax} passenger{bookingPax > 1 ? 's' : ''}</div>
                            </div>
                            <div><strong className="text-primary fs-4">{formatCurrency(totalAmount)}</strong></div>
                          </div>
                        </div>

                        {/* Error Message */}
                        {errorMessage && (
                          <div className="alert alert-danger mb-3">
                            <RiAlertLine className="me-2" />
                            {errorMessage}
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="d-grid gap-2">
                          {!bookingId ? (
                            <button
                              className="btn btn-warning btn-lg"
                              onClick={initiateBookingFlow}
                              disabled={isProcessing}
                            >
                              {isProcessing ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Creating Block Request...
                                </>
                              ) : (
                                <>
                                  <RiCheckboxCircleLine className="me-2" />
                                  Block Seats & Continue
                                </>
                              )}
                            </button>
                          ) : bookingDetails?.bookingStatus === "BLOCK REQUEST" ? (
                            <>
                              <button
                                className="btn btn-warning btn-lg"
                                onClick={handleViewBookingStatus}
                                disabled={checkingStatus}
                              >
                                {checkingStatus ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Checking Status...
                                  </>
                                ) : (
                                  <>
                                    <RiRefreshLine className="me-2" />
                                    Check DMC Status
                                  </>
                                )}
                              </button>
                              <button
                                className="btn btn-outline-danger"
                                onClick={cancelBlockRequest}
                                disabled={isProcessing}
                              >
                                <RiCloseCircleLine className="me-2" />
                                Cancel Block Request
                              </button>
                            </>
                          ) : bookingDetails?.bookingStatus === "BLOCKED" ? (
                            <button
                              className="btn btn-success btn-lg"
                              onClick={initiatePayment}
                              disabled={isProcessing}
                            >
                              {isProcessing ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Processing Payment...
                                </>
                              ) : (
                                <>
                                  <RiLockUnlockLine className="me-2" />
                                  Pay {formatCurrency(totalAmount)}
                                </>
                              )}
                            </button>
                          ) : bookingDetails?.bookingStatus === "REJECTED" ? (
                            <button
                              className="btn btn-primary"
                              onClick={handleRetry}
                            >
                              <RiRefreshLine className="me-2" />
                              Try Another Fare
                            </button>
                          ) : null}
                        </div>

                        <div className="mt-3 text-center">
                          <div className="small text-success">
                            <RiShieldLine className="me-1" />
                            100% Secure Payment
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Booking Success */}
            {bookingSuccess && (
              <div className="row">
                <div className="col-12">
                  <div className="card border-success">
                    <div className="card-body text-center py-5">
                      <div className="display-1 text-success mb-3"><RiCheckDoubleLine /></div>
                      <h4 className="text-success mb-3">Payment Successful!</h4>
                      <p className="mb-4">Your booking has been confirmed. Redirecting to confirmation page...</p>
                      <div className="spinner-border text-primary" role="status"></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Booking Failed */}
            {bookingFailed && (
              <div className="row">
                <div className="col-12">
                  <div className="card border-danger">
                    <div className="card-body text-center py-5">
                      <div className="display-1 text-danger mb-3"><RiCloseCircleLine /></div>
                      <h4 className="text-danger mb-3">Payment Failed</h4>
                      <p className="mb-2">{errorMessage}</p>
                      <div className="d-flex justify-content-center gap-3 mt-4">
                        <button className="btn btn-primary" onClick={handleRetry}>Try Again</button>
                        <button className="btn btn-outline-secondary" onClick={handleBack}>Back to Results</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .spinning {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SeriesFarePayment;